"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * FilterChip component - a controlled toggle button for filter selections.
 *
 * Key design decisions:
 * 1. Controlled state via `pressed` prop - no internal state
 * 2. Hover styles ONLY apply on devices with BOTH hover capability AND fine pointer
 *    via @media(hover:hover) and (pointer:fine). This prevents sticky hover on mobile
 *    where some devices report hover:hover but have coarse (touch) primary pointer.
 * 3. Active (pressed) and hover states are visually distinct:
 *    - Active: ring-2 (thicker border)
 *    - Hover (desktop only): ring-1 (thin border)
 *
 * Accessibility:
 * - aria-pressed reflects the toggle state (always controlled, cannot be overridden)
 * - data-state="on"/"off" for CSS selector compatibility (always controlled)
 * - Keyboard navigation: Enter/Space to toggle (native button behavior)
 * - Focus visible ring indicators
 *
 * Click behavior:
 * - Consumer onClick is called first
 * - If event.defaultPrevented, toggle is skipped (allows consumers to conditionally prevent)
 * - Otherwise, onPressedChange is called with the toggled state
 *
 * Used for filter chips in contribution breakdowns.
 */
export interface FilterChipProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "aria-pressed"
> {
  /** Current pressed/selected state (controlled) */
  pressed: boolean;
  /** Callback when pressed state should change */
  onPressedChange: (pressed: boolean) => void;
  /** Whether the chip should have clickable styles (default: true) */
  isClickable?: boolean;

  /**
   * Controlled attribute - cannot be overridden via props.
   * Use `pressed` prop instead.
   * @internal
   */
  "data-state"?: never;
}

const FilterChip = React.forwardRef<HTMLButtonElement, FilterChipProps>(
  (
    {
      className,
      pressed,
      onPressedChange,
      isClickable = true,
      disabled,
      children,
      onClick,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      // Call consumer onClick first - allows preventing toggle via event.preventDefault()
      onClick?.(event);

      // Skip toggle if consumer prevented default or if not interactive
      if (event.defaultPrevented) return;
      if (disabled || !isClickable) return;

      onPressedChange(!pressed);
    };

    return (
      <button
        ref={ref}
        type="button"
        {...props}
        // Controlled attributes must come AFTER spread to prevent override
        aria-pressed={pressed}
        data-state={pressed ? "on" : "off"}
        disabled={disabled || !isClickable}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        className={cn(
          // Base styles
          "group col-span-2 -mx-2 grid grid-cols-subgrid items-center rounded-xl px-2 py-1.5 text-left transition-colors",
          // Clickable/interactive styles
          isClickable && [
            "cursor-pointer",
            // Focus visible
            "focus-visible:ring-ring/70 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent focus-visible:outline-none",
            // Hover styles - ONLY on devices that support BOTH hover AND fine pointer
            // Uses @media (hover: hover) and (pointer: fine) to prevent sticky hover on mobile
            // Some Android devices report hover:hover=true but have coarse (touch) primary pointer
            "[@media(hover:hover)_and_(pointer:fine)]:hover:bg-accent/50 [@media(hover:hover)_and_(pointer:fine)]:hover:ring-brand/30 [@media(hover:hover)_and_(pointer:fine)]:hover:shadow-sm [@media(hover:hover)_and_(pointer:fine)]:hover:ring-1",
            "dark:[@media(hover:hover)_and_(pointer:fine)]:hover:bg-accent/30 dark:[@media(hover:hover)_and_(pointer:fine)]:hover:ring-brand/25",
          ],
          // Active/pressed state styles via data attribute selectors
          // These have higher visual weight (ring-2) than hover (ring-1)
          "data-[state=on]:bg-accent/50 data-[state=on]:ring-brand/30 data-[state=on]:shadow-sm data-[state=on]:ring-2",
          "dark:data-[state=on]:bg-accent/30 dark:data-[state=on]:ring-brand/25",
          // Not clickable
          !isClickable && "cursor-default",
          className,
        )}
      >
        {children}
      </button>
    );
  },
);
FilterChip.displayName = "FilterChip";

export { FilterChip };
